import {BASIS} from "/@back@/commons/basis.js";
import {CellBuilderString, GridDataHolderBase, GridDataHolderJsonArray} from "/@back@/commons/widgets/grid-libs.js";
import {Grid, OGridSmallInit} from "/@back@/commons/widgets/grid-tags.js";
import {GridArrayToTree, GridDataHolderJsonTree, IGridCategorizer} from "/@back@/commons/widgets/tree.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {AccelKeyMgr, ACTION, IAction} from "/@lib@/commons/actions.js";
import {IActionable, IContextMenuActions, IContextMenuActionsPointer, IFocusActionables} from "/@back@/commons/actionables.js";
import {ActionBtn} from "/@back@/commons/widgets/buttons.js";
import {IExecutorInUniversePointer, IJobsCtx, JJobBase} from "/@lib@/core/executor.js";
import {GridColDef, IGridDataRow, IGridLineDrawer} from "/@back@/commons/widgets/grid-core.js";

/**
 * Widget d'affichage d'une liste de jobs
 */
export interface JobsGrid extends Grid {
	initialize(init?: OJobsGridInit<IJobGridCtx>): this

	/** Fournisseur des données courantes (srcGridDatas ou celui issu du categorizer). */
	get dataHolder(): GridDataHolderBase<JJobBase>
	set dataHolder(val: GridDataHolderBase<JJobBase>)
}

export interface OJobsGridInit<E extends IJobGridCtx> extends OGridSmallInit {
	reg?: IReg<IExecutorInUniversePointer>

	/** Actions associées aux items sélectionnés (menu contextuel et IFocusActionables). */
	actions?: IAction<IJobGridCtx>[]

	/** Action exécutée au double-click ou sur les frappes entrée ou espace. */
	defaultAction?: IAction<IJobGridCtx>

	selectAction?: IAction<IJobGridCtx>

	/** Racourcis claviers à capter sur la sel courante. */
	accelKeyMgr?: AccelKeyMgr<IJobGridCtx>

	dataHolder?: IJobGridDatas
}


/**
 *
 */

export class JobsGrid extends Grid implements IJobGridCtx, IFocusActionables<IJobGridCtx>, IContextMenuActionsPointer<IJobGridCtx>, IGridLineDrawer {

	reg: IReg<IExecutorInUniversePointer>;

	config: OJobsGridInit<IJobGridCtx>;

	accelKeyMgr: AccelKeyMgr<IJobGridCtx>;

	/** Mémoire des actions pour le menuContextuel. */
	actions?: IAction<IJobGridCtx>[];

	/**
	 * Datas à plat, provider pour un Categorizer ou dataHolder direct pour ce grid.
	 * A utiliser pour les mises à jour des donnnées (et pas dataHolder).
	 */
	jobsGridDatas: IJobGridDatas;

	/** API IJobGridCtx */
	jobs: JJobBase[];

	protected _treeView: GridArrayToTree<unknown, JJobBase>;

	/** Categorizer */
	get categorizer(): IGridCategorizer<JJobBase, JJobBase> {
		return this._treeView ? this._treeView.categorizer : null;
	}

	setCategorizer(categorizer: IGridCategorizer<JJobBase, JJobBase>, target?: GridDataHolderJsonTree<JJobBase>) {
		if (categorizer) {
			if (!this._treeView) this._treeView = new GridArrayToTree(target).setProvider(this.jobsGridDatas);
			this._treeView.setCategorizer(categorizer);
			this.dataHolder = this._treeView.target;
		} else if (this._treeView) {
			//On supprime le categorizer
			this._treeView.setProvider(null);
			this._treeView = null;
			this.dataHolder = this.jobsGridDatas;
		}
	}

	protected async _initialize(init: OJobsGridInit<IJobGridCtx>): Promise<void> {
		this.config = init || {};
		this.reg = this.findReg(init);

		// Valeur par défaut des params
		if (!init.dataHolder) init.dataHolder = new JobGridDatas();

		this.dataHolder = new GridDataHolderJsonTree<JJobBase>('children');
		if (!init.selType) init.selType = 'multi';
		if (!init.columnDefs) init.columnDefs = this.getDefaultColumnDefs();
		init.defaultActionCtx = this;
		if (!('lineDrawer' in init)) init.lineDrawer = this;
		this.jobsGridDatas = init.dataHolder;

		super._initialize(init);

		if (this.localName !== 'server-jobs-grid') this.findReg(init).installSkin('server-jobs-grid', this.shadowRoot);

		this.addEventListener('grid-select', function (this: JobsGrid, ev: Event) {
			this.jobs = this.dataHolder.getSelectedDatas();
			//console.log("jobs::::", this.jobs);
			if (this.matches(':focus')) this.dispatchEvent(new CustomEvent<IFocusActionables<IJobGridCtx>>('c-focus-actions', {detail: this, bubbles: true, composed: true}));
			if (init.selectAction) init.selectAction.executeIfAvailable(this);
		});

		if (init.actions) this.actions = ACTION.injectSepByGroup(init.actions, this.reg.getPref("groupOrder.jobs", ""), this);
		if (init.accelKeyMgr) {
			this.accelKeyMgr = init.accelKeyMgr;
			this.addEventListener('keydown', (ev: KeyboardEvent) => {
				this.accelKeyMgr.handleKeyboardEvent(ev, this);
			});
		}

	}

	getDefaultColumnDefs() {
		return [new GridColDef('id')
			.setDefaultSort(1, 'ascendant')
			.setFlex('1rem', 1, 1).setMinWidth('8em').setSortable(true)
			.setCellBuilder(new CellBuilderString("id"))
		];
	}

	buildInitFromAtts(init?: any): any {
		init = super.buildInitFromAtts(init);
		if (this.hasAttribute("actions")) init.actions = this.reg.mergeLists(...BASIS.extractAttr(this, 'actions').split(' '));
		return init;
	}

	selectJobId(id: string): boolean {
		return this._selectRowKey(this.jobsGridDatas.findRowKeyByJobId(id));
	}

	private _selectRowKey(rowKey: JJobBase): boolean {
		if (!rowKey) return false;
		if (this._treeView && this._treeView.categorizer) {
			const id = this._treeView.categorizer.getId(rowKey);
			rowKey = this._treeView.getTargetEntryById(id);
		}
		const offset = this.dataHolder.getOffset(rowKey);
		if (offset < 0) return false;
		this.setSelectedRows(offset);
		return true;
	}

	ensureRowVisibleByJobId(id: string): boolean {
		const rowKey = this.jobsGridDatas.findRowKeyByJobId(id);
		if (rowKey) this.ensureRowVisible(this.dataHolder.getOffset(rowKey));
		return rowKey != null;
	}


	redrawLine(row: IGridDataRow, line: HTMLElement): void {
	}

	getSelectedJob(): JJobBase | null {
		if (this.dataHolder) {
			const row = this.dataHolder.getRow(this.getSelectedRow());
			if (!row) return null;
			return row.rowDatas;
		}
	}

	getSelectedJobs(): JJobBase[] {
		return (this.dataHolder && this.dataHolder.countRows() > 0) ? this.dataHolder.getSelectedDatas() as JJobBase[] : [];
	}

	/**
	 * Sélection des entrées de l'arbre via une liste de JJobBase sur un tree **chargé**
	 * Si une netrée demandée n'est pas présent dans le tree, aucune erreur n'est remontée
	 * @param JJobBase[]
	 */
	selectByJob(jobs: JJobBase[]): void {
		if (jobs != null) {
			const selectedRows: number[] = [];
			for (let i = 0, c = this.dataHolder.countRows(); i < c; i++) {
				const jobRow = this.dataHolder.getRow(i).rowDatas as JJobBase;
				if (jobRow && jobs.find(job => {
					if (job && job.id == jobRow.id)
						return true;
				})) {
					selectedRows.push(i);
				}
			}
			if (selectedRows.length > 0)
				this.setSelectedRows(selectedRows);
			else
				this.clearSel();
		}
	}

	/** boutons à afficher dans la focusToolbar. */
	protected _focusActionables: IActionable<IJobGridCtx>[];
	get focusActionables(): IActionable<IJobGridCtx>[] {
		if (!this._focusActionables && this.actions) this._focusActionables = ActionBtn.buildButtons<IJobGridCtx>(this.actions, this, 'bar');
		return this._focusActionables;
	};

	get ctxMenuActions(): IContextMenuActions<IJobGridCtx> | null {return {actions: this.actions, actionContext: this, rect: this.getSelRect()}}

	//*** API IJobsGridCtx / IShortDescCtx ***

	get me() {return this} // Astuce pour retrouver le SpaceTree quand l'actionContext a été surchargé.


}

REG.reg.registerSkin('server-jobs-grid', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
	  min-height: 0;
	  min-width: 0;
	  flex-direction: column;
  }

  :focus-visible {
	  outline: var(--focus-outline);
  }

  #head {
	  border-bottom: 1px solid var(--border-color);
  }

  #filter {
	  background: .1em / 1em no-repeat url(/@skin@/commons/icons/filter.svg);
	  padding-block: 2px;
	  padding-inline: 1.2em 2px;
	  display: flex;
	  min-width: 0;
	  min-height: 0;
	  align-items: center;
	  overflow: hidden;
	  flex-wrap: wrap;
  }

  #filter > * {
	  margin-block: 0;
	  margin-inline: .3rem 0;
  }

  #filter > select {
	  border: none;
  }

  #filter > input {
	  min-width: 10em;
	  flex: 1;
	  background-color: var(--form-search-bgcolor);
	  color: var(--form-color);
	  border: 1px solid var(--border-color);
	  font-size: inherit;
  }

  #filterType {
	  background-image: url("/@skin@/core/objects/userGroup.svg");
	  background-color: var(--form-bgcolor);
	  background-repeat: no-repeat;
	  background-size: auto 13px;
	  background-position-y: center;
	  background-position-x: 3px;
	  min-width: 37px;
	  max-width: 37px;
	  padding-inline-start: 37px;
  }

  #filterType[data-value=group] {
	  background-image: url("/@skin@/core/objects/group.svg");
  }

  #filterType[data-value=user] {
	  background-image: url("/@skin@/core/objects/user.svg");
  }

  #filterGroup {
	  min-width: 0;
  }

  c-input-users-panel {
	  min-width: 12rem;
  }

  #msg {
	  font-size: .8rem;
	  color: var(--info-color);
	  text-align: end;
	  margin-inline-end: .5rem;
	  font-style: italic;
  }

  c-grid {
	  flex: 1;
	  border: none;
	  background-color: var(--row-bgcolor);
  }
`);

customElements.define("server-jobs-grid", JobsGrid);


/**
 * Contexte pour les actions du JobsGrid.
 * Abstraction de JobsGrid pour être utilisé en contexte d'actions wrappées/surchagées.
 */
export interface IJobGridCtx extends IJobsCtx {
	me: JobsGrid,
}

/** Api de la source de données poyr un JobGrid. */
interface IJobGridDatas extends GridDataHolderJsonArray<JJobBase> {
	findRowKeyByJobId(id: string): JJobBase;

	removeByJobId(id: string): void;

	refreshFields(rowKey: JJobBase, newFields: JJobBase): void;
}

export class JobGridDatas extends GridDataHolderJsonArray<JJobBase> implements IJobGridDatas {

	useLiveSrcUri: boolean;

	constructor(/*reg: IReg<IWspUiEnv>, srcFilter?: (data: JJobBase) => boolean*/) {
		super();
	}

	findRowKeyByJobId(id: string): JJobBase {
		for (const d of this._datas) if (d.id === id) return d;
		return null;
	}

	removeByJobId(id: string) {
		let jobIndex = this._datas.findIndex((job) => job.id === id);
		this.updateDatas(jobIndex, 1);
	}

	refreshFields(rowKey: JJobBase, newFields: JJobBase) {
		if (this.onWillUpdate) {
			//listeners déclarés, on passe par l'api pour le dispatch et un remplacement strict de l'objet pour éviter tout effet de bord.
			const idx = this.getOffset(rowKey);
			if (idx >= 0) this.updateDatas(idx, 1, Object.assign(Object.create(null), newFields));
		} else {
			//version optimisée XXX A mesurer...
			for (const k in rowKey) (rowKey as Dict<any>)[k] = undefined;
			Object.assign(rowKey, newFields);
			const grid = this.grid;
			if (grid) grid.invalidateRows();
		}
	}


}